import { ArrowRight, Lock } from "lucide-react";
import { useMemo } from "react";

import {
  DataTable,
  type DataTableColumn,
} from "@/components/cinch/DataTable/DataTable";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

import type { AssumptionsPayload, EventKind } from "../api";
import { KIND_LABEL, STATE_LABEL } from "../lib";
import { fmtCur2, fmtDate } from "../../renewals/format";

/** One flattened row across every assumption type, so the table under the
 *  calendar reads as a single list rather than five. */
interface AssumptionRow {
  id: string;
  kind: EventKind;
  title: string;
  detail: string | null;
  effective: string | null;
  effectiveLabel: string;
  cash: string;
  cashLabel: string;
  amount: number;
  state: string | null;
  /** True when the monthly close owns this row and editing it here won't stick. */
  locked: boolean;
}

export function flatten(data: AssumptionsPayload | undefined): AssumptionRow[] {
  if (!data) return [];
  const rows: AssumptionRow[] = [];

  for (const h of data.headcount) {
    rows.push({
      id: `headcount:${h.id}`,
      kind: "headcount",
      title: h.name,
      detail: [h.role, h.department].filter(Boolean).join(" · ") || null,
      effective: h.start_date,
      effectiveLabel: "Starts",
      // The cash date is a pay-run lookup, so the table shows the accrual date
      // and lets the calendar draw the hop.
      cash: h.start_date,
      cashLabel: "Next pay run",
      amount: h.plan_state === "baked_in" ? 0 : h.add_per_paycheck,
      state: h.plan_state,
      locked: false,
    });
  }
  for (const c of data.churn) {
    rows.push({
      id: `churn:${c.id}`,
      kind: "churn",
      title: c.account_name,
      detail: c.reason,
      effective: c.notice_date,
      effectiveLabel: "Notice",
      cash: c.end_date,
      cashLabel: "Billing ends",
      amount: -c.mrr,
      state: c.state,
      locked: c.confirmed_by_recon,
    });
  }
  for (const o of data.one_offs) {
    rows.push({
      id: `one_off:${o.id}`,
      kind: "one_off",
      title: o.label,
      detail: o.bucket_name ?? "no bucket — not in the forecast",
      effective: o.effective_date,
      effectiveLabel: "Arises",
      cash: o.cash_date,
      cashLabel: "Cash",
      amount: o.amount,
      state: o.certainty,
      locked: false,
    });
  }
  for (const v of data.vendors) {
    rows.push({
      id: `vendor:${v.id}`,
      kind: "vendor",
      title: v.vendor,
      detail: v.notes,
      effective: v.due_date,
      effectiveLabel: "Due",
      cash: v.cash_week,
      cashLabel: "Clears",
      amount: v.forecast_amount,
      state: null,
      locked: false,
    });
  }
  for (const r of data.revenue) {
    rows.push({
      id: `new_revenue:${r.id}`,
      kind: "new_revenue",
      title: r.contract,
      detail: `net ${r.terms_days}${r.buffer_days ? ` +${r.buffer_days}d observed` : ""}`,
      effective: r.start_date,
      effectiveLabel: "Billing effective",
      cash: r.first_cash_week,
      cashLabel: "First cash",
      amount: r.annual_amount / 12,
      state: null,
      locked: false,
    });
  }

  return rows.sort((a, b) => a.cash.localeCompare(b.cash));
}

/**
 * Every assumption in one table, sorted by when the money moves.
 *
 * The two date columns sit next to each other deliberately: reading down them
 * shows how far apart "it happened" and "we paid for it" actually are.
 */
export function AssumptionsTable({
  data,
  loading,
  error,
  filterKinds,
  onSelect,
}: {
  data: AssumptionsPayload | undefined;
  loading: boolean;
  error: unknown;
  filterKinds: Set<EventKind>;
  onSelect: (id: string) => void;
}) {
  const rows = useMemo(
    () => flatten(data).filter((r) => filterKinds.has(r.kind)),
    [data, filterKinds],
  );

  const columns: DataTableColumn<AssumptionRow>[] = [
    {
      key: "kind",
      header: "Type",
      render: (r) => <Badge variant="neutral">{KIND_LABEL[r.kind]}</Badge>,
    },
    {
      key: "title",
      header: "What",
      className: "max-w-[240px]",
      render: (r) => (
        <div className="min-w-0">
          <span className="flex items-center gap-1 truncate text-sm font-semibold">
            {r.title}
            {r.locked && (
              <Lock
                className="h-3 w-3 shrink-0 text-success"
                aria-label="Settled by the monthly close"
              />
            )}
          </span>
          {r.detail && (
            <span className="block truncate text-xs text-muted-foreground">
              {r.detail}
            </span>
          )}
        </div>
      ),
    },
    {
      key: "dates",
      header: "Happens → cash",
      render: (r) => (
        <div className="flex items-center gap-1.5 text-xs">
          <span className="text-muted-foreground">
            {r.effective ? fmtDate(r.effective) : "—"}
          </span>
          <ArrowRight className="h-3 w-3 text-muted-foreground" />
          <span className="font-medium">{fmtDate(r.cash)}</span>
        </div>
      ),
    },
    {
      key: "state",
      header: "Confidence",
      render: (r) =>
        r.state ? (
          <Badge
            variant={
              r.state === "speculative"
                ? "neutral"
                : r.state === "confirmed" || r.state === "committed"
                  ? "success"
                  : "warning"
            }
          >
            {STATE_LABEL[r.state] ?? r.state}
          </Badge>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      key: "amount",
      header: "Amount",
      numeric: true,
      render: (r) => (
        <span
          className={cn(
            "font-semibold tabular-nums",
            r.amount < 0 ? "text-danger" : r.amount > 0 ? "text-success" : "text-muted-foreground",
          )}
          title={
            r.amount === 0 && r.state === "baked_in"
              ? "Already inside the pay-run amounts — adds nothing further"
              : undefined
          }
        >
          {r.amount ? fmtCur2(r.amount) : "—"}
        </span>
      ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={rows}
      rowKey={(r) => r.id}
      loading={loading}
      error={error ? String(error) : null}
      onRowClick={(r) => onSelect(r.id)}
      emptyMessage="No assumptions yet. Click a day on the calendar to add one."
      containerClassName="max-h-[420px]"
    />
  );
}
